// Entry File Path Tool - Manage app entry file path setting
// Extends WorkspaceTool - uses app context and workspace manager

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class EntryFilePathTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "entryFilePathTool";
  }

  /**
   * Resolve app ID from args or workspace manager
   */
  resolveAppId(appId) {
    if (appId) {
      return String(appId);
    }
    const managerAppId =
      this.workspaceManager?.app?.id ||
      this.workspaceManager?.app?._id ||
      null;
    return managerAppId ? String(managerAppId) : null;
  }

  /**
   * Get entry file path for an app
   * @param {string} [appId] - App ID (optional, defaults to current workspace app)
   */
  async getEntryFilePath(appId = null) {
    try {
      const resolvedAppId = this.resolveAppId(appId);
      if (!resolvedAppId) {
        return this.returnError({}, "App ID is required");
      }

      const response = await fetch(
        `/api/apps/${encodeURIComponent(resolvedAppId)}/settings/entryFilePath`,
        { credentials: "include" }
      );
      const data = await response.json();

      if (!response.ok || !data?.success) {
        return this.returnError(
          {},
          data?.error || "Failed to fetch entry file path"
        );
      }

      return this.returnSuccess(
        { appId: resolvedAppId, entryFilePath: data?.value ?? null },
        "Entry file path retrieved"
      );
    } catch (error) {
      logger.error(
        { tags: "tools|entryfile|get|error", color1: "red" },
        "Failed to get entry file path",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Set entry file path for an app
   * @param {string} [appId] - App ID (optional, defaults to current workspace app)
   * @param {string|null} entryFilePath - New entry file path (null clears)
   */
  async setEntryFilePath(appId = null, entryFilePath = null) {
    try {
      const resolvedAppId = this.resolveAppId(appId);
      if (!resolvedAppId) {
        return this.returnError({}, "App ID is required");
      }

      let normalizedPath = entryFilePath;
      if (normalizedPath !== null && normalizedPath !== undefined) {
        if (typeof normalizedPath !== "string") {
          return this.returnError({}, "entryFilePath must be a string or null");
        }
        normalizedPath = normalizedPath.trim();
        if (normalizedPath.length === 0) {
          normalizedPath = null;
        }
      } else {
        normalizedPath = null;
      }

      const response = await fetch(
        `/api/apps/${encodeURIComponent(resolvedAppId)}/settings/entryFilePath`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ value: normalizedPath }),
        }
      );
      const data = await response.json();

      if (!response.ok || !data?.success) {
        return this.returnError(
          {},
          data?.error || "Failed to update entry file path"
        );
      }

      if (this.workspaceManager?.app) {
        const app = this.workspaceManager.app;
        app.settings = app.settings || {};
        app.settings.entryFilePath = normalizedPath;
      }

      return this.returnSuccess(
        { appId: resolvedAppId, entryFilePath: normalizedPath },
        "Entry file path updated"
      );
    } catch (error) {
      logger.error(
        { tags: "tools|entryfile|set|error", color1: "red" },
        "Failed to set entry file path",
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
